import { useState, useEffect } from 'react';
import {
  Users,
  AlertCircle,
  Shield,
  UserCheck,
  Clock,
  Mail,
  Star,
  Trash2,
  RotateCcw,
  UserX,
  X
} from 'lucide-react';
import api from '../services/api';
import { useAuthStore } from '../context/authStore';
import { useCompanyStore } from '../context/companyStore';
import { isInternalRole } from '../utils/roles.js';

const roleBadges = {
  admin: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300',
  technician: 'bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300',
  company_admin: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300',
  billing: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300',
  team_member: 'bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300',
  end_user: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300',
  user: 'bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300',
};

export default function TeamPage() {
  const { user: currentUser, can } = useAuthStore();
  const { companies } = useCompanyStore();
  // The companies this person manages a team at (the role held there grants
  // it); one is picked when there are several.
  const managedCompanies = companies.filter((c) => can('company_users', 'manage', c.id));
  const [companyId, setCompanyId] = useState(null);
  const effectiveCompanyId = companyId
    || managedCompanies.find((c) => c.isPrimary)?.id
    || managedCompanies[0]?.id
    || currentUser?.cwCompanyId
    || null;
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Role change modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [updatingRole, setUpdatingRole] = useState(false);
  // The customer roles the MSP defines (built-in and custom), from the server.
  const [roles, setRoles] = useState([]);
  const roleName = (name) => roles.find((r) => r.name === name)?.displayName || String(name || '').replace(/_/g, ' ');

  // Remove user modal
  const [removeUser, setRemoveUser] = useState(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    fetchTeam();
  }, [effectiveCompanyId]);

  const params = { params: effectiveCompanyId ? { companyId: effectiveCompanyId } : {} };

  const fetchTeam = async () => {
    setLoading(true);
    setError(null);

    try {
      const [response, rolesResponse] = await Promise.all([api.get('/company/users', params), api.get('/company/roles', params)]);
      setRoles(rolesResponse.data.roles || []);
      setCompany(response.data.company);
      setUsers(response.data.users);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async () => {
    if (!selectedUser || newRole === selectedUser.role) return;

    setUpdatingRole(true);
    try {
      await api.patch(`/company/users/${selectedUser.id}/role`, { role: newRole }, params);
      setUsers(users.map(u =>
        u.id === selectedUser.id ? { ...u, role: newRole } : u
      ));
      setSelectedUser(null);
      setNewRole('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update role');
    } finally {
      setUpdatingRole(false);
    }
  };

  const handleRemoveUser = async () => {
    if (!removeUser) return;

    setRemoving(true);
    try {
      const res = await api.delete(`/company/users/${removeUser.id}`, params);
      // Somebody from another company loses this company; a member of this
      // company is deactivated and stays listed as Removed.
      setUsers(res.data?.accessRemoved
        ? users.filter((u) => u.id !== removeUser.id)
        : users.map(u => (u.id === removeUser.id ? { ...u, is_active: false } : u)));
      setRemoveUser(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove user');
    } finally {
      setRemoving(false);
    }
  };

  const [restoringId, setRestoringId] = useState(null);
  const handleRestoreUser = async (user) => {
    setRestoringId(user.id);
    try {
      await api.post(`/company/users/${user.id}/restore`, {}, params);
      setUsers(users.map(u => (u.id === user.id ? { ...u, is_active: true } : u)));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to restore user');
    } finally {
      setRestoringId(null);
    }
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-white">Team Management</h1>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
            Manage users from {company?.name || 'your company'}
          </p>
        </div>
        {managedCompanies.length > 1 && (
          <label className="text-sm text-neutral-600 dark:text-neutral-300 flex items-center gap-2">
            Company
            <select
              value={effectiveCompanyId || ''}
              onChange={(e) => setCompanyId(Number(e.target.value))}
              className="input"
            >
              {managedCompanies.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between text-red-700 dark:text-red-300">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p>{error}</p>
          </div>
          <button onClick={() => setError(null)} className="hover:text-red-900 dark:hover:text-red-200">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats */}
      {company && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-neutral-900 dark:text-white">{company.userCount}</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Total Users</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold text-neutral-900 dark:text-white">{company.adminCount}</p>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Admins</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 dark:bg-neutral-700/50 border-b border-neutral-200 dark:border-neutral-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-neutral-500 dark:text-neutral-400">
                    <Users className="w-8 h-8 mx-auto mb-2 text-neutral-400 dark:text-neutral-500" />
                    No team members found
                  </td>
                </tr>
              ) : (
                users.map((user) => {
                  const isCurrentUser = user.id === currentUser?.id;
                  const isInternalStaff = isInternalRole(user.role);
                  const canManage = !isCurrentUser && !isInternalStaff && user.role !== 'company_admin';

                  return (
                    <tr key={user.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-700/50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center text-primary-600 dark:text-primary-400 font-medium text-sm">
                            {user.first_name?.[0]}{user.last_name?.[0]}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-neutral-900 dark:text-white">
                                {user.first_name} {user.last_name}
                              </p>
                              {user.is_primary_contact && (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-300 rounded">
                                  <Star className="w-3 h-3" />
                                  Primary
                                </span>
                              )}
                              {isCurrentUser && (
                                <span className="px-1.5 py-0.5 text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded">
                                  You
                                </span>
                              )}
                              {user.is_primary_company === false && (
                                <span
                                  className="px-1.5 py-0.5 text-xs font-medium bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 rounded"
                                  title="Their account belongs to another company; this is the role they hold here"
                                >
                                  From another company
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-sm text-neutral-500 dark:text-neutral-400">
                              <Mail className="w-3 h-3" />
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${roleBadges[user.role] || roleBadges.user}`}>
                          {roleName(user.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {user.is_active === false ? (
                          <span className="inline-flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
                            <UserX className="w-4 h-4" />
                            Removed
                          </span>
                        ) : user.email_verified ? (
                          <span className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400">
                            <UserCheck className="w-4 h-4" />
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-sm text-yellow-600 dark:text-yellow-400">
                            <Clock className="w-4 h-4" />
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-neutral-600 dark:text-neutral-300">
                        {formatDate(user.last_login)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {canManage && user.is_active === false ? (
                          <button
                            onClick={() => handleRestoreUser(user)}
                            disabled={restoringId === user.id}
                            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 font-medium disabled:opacity-50"
                          >
                            <RotateCcw className="w-4 h-4" />
                            {restoringId === user.id ? 'Restoring...' : 'Restore'}
                          </button>
                        ) : !isCurrentUser && !isInternalStaff && (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => openRoleModal(user)}
                              className="text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                            >
                              Change Role
                            </button>
                            {canManage && (
                              <button
                                onClick={() => setRemoveUser(user)}
                                className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30 rounded-lg"
                                title="Remove user"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role Change Modal */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSelectedUser(null)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Change Role</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  {selectedUser.first_name} {selectedUser.last_name}
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              {roles.map((r) => (
                <label
                  key={r.name}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    newRole === r.name
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                      : 'border-neutral-200 dark:border-neutral-600 hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="role"
                    value={r.name}
                    checked={newRole === r.name}
                    onChange={(e) => setNewRole(e.target.value)}
                    className="sr-only"
                  />
                  <span className={`shrink-0 px-2 py-1 text-xs font-medium rounded ${roleBadges[r.name] || roleBadges.end_user}`}>
                    {r.displayName}
                  </span>
                  {r.description && <span className="text-sm text-neutral-600 dark:text-neutral-300 min-w-0">{r.description}</span>}
                </label>
              ))}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setSelectedUser(null)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleRoleChange}
                disabled={updatingRole || newRole === selectedUser.role}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
              >
                {updatingRole ? 'Updating...' : 'Update Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove User Modal */}
      {removeUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setRemoveUser(null)} />
          <div className="relative bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Remove User</h3>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">
                  This will deactivate their portal access
                </p>
              </div>
            </div>

            {removeUser.is_primary_company === false ? (
              <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-6">
                Are you sure you want to remove{' '}
                <strong className="dark:text-white">{removeUser.first_name} {removeUser.last_name}</strong> ({removeUser.email})
                from this company? Their account belongs to another company and keeps
                working there; they lose access to this one. Only your support
                provider can add it back.
              </p>
            ) : (
              <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-6">
                Are you sure you want to remove{' '}
                <strong className="dark:text-white">{removeUser.first_name} {removeUser.last_name}</strong> ({removeUser.email})
                from the portal? They are signed out everywhere straight away and cannot
                register again. You can restore them from this page later.
              </p>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setRemoveUser(null)}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveUser}
                disabled={removing}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {removing ? 'Removing...' : 'Remove User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
